// src/pages/ResourceFormPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { resourcesAPI, companiesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, Upload, FileText, Trash2, Plus } from 'lucide-react';


const EMP_TYPES      = ['CDI','INTERCO','STAGIAIRE','ALTERNANT','COMMERCIAL','EXTERNE','FREELANCE'];
const STATUT_RESSOURCE = ['ACTIF','EN_MISSION','FUTURE_ARRIVEE','INTERNE','EXTERNE','INTERCONTRAT','CONGE','INACTIF','SORTI'];
const MISSION_STATUTS  = ['EN_COURS','FUTURE','TERMINEE','SUSPENDUE'];
const STATUSES   = STATUT_RESSOURCE;
const WORK_MODES = ['PRESENTIEL','HYBRID','REMOTE'];


export default function ResourceFormPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isEdit   = !!id;
  // Seul un super admin peut modifier le statut de la ressource
  const isSuperAdmin = currentUser?.is_superuser === true || (currentUser?.role || '').toUpperCase() === 'SUPER_ADMIN';
  const [saving,    setSaving]    = useState(false);
  const [companies, setCompanies] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const originalStatusRef = useRef('ACTIF');
  const [form, setForm] = useState({
    full_name:'', first_name:'', last_name:'', civilite:'', title:'',
    employee_type:'CDI', status:'ACTIF',
    email:'', phone:'', linkedin_link:'', location:'',
    availability:'', experience_years:'', tjm:'', salary:'',
    work_mode:'', mobility:'', contract_type:'',
    current_client:'', current_mission:'', mission_start_date:'', mission_end_date:'',
    start_date:'', end_date:'',
    manager_name:'', recruiter_name:'',
    summary:'', internal_comment:'',
  });


  useEffect(() => {
    companiesAPI.list({}).then(r => setCompanies(r.data || [])).catch(() => {});
    if (!isEdit) return;
    resourcesAPI.getOne(id).then(r => { setForm(r.data); originalStatusRef.current = r.data?.status || 'ACTIF'; }).catch(() => { toast.error('Erreur'); navigate('/resources'); });
    resourcesAPI.listDocuments(id).then(r => setDocuments(r.data || [])).catch(() => {});
  }, [id]);


  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!id) { toast('Sauvegardez d\'abord la fiche, puis uploadez les documents', { icon: 'ℹ️' }); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name);
      fd.append('document_type', 'CV');
      await resourcesAPI.uploadDocument(id, fd);
      const r = await resourcesAPI.listDocuments(id);
      setDocuments(r.data || []);
      toast.success('Document uploadé');
    } catch { toast.error('Erreur upload'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDeleteDoc = async (docId) => {
    try { await resourcesAPI.deleteDocument(docId); const r = await resourcesAPI.listDocuments(id); setDocuments(r.data || []); toast.success('Supprimé'); }
    catch { toast.error('Erreur'); }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name) { toast.error('Nom complet obligatoire'); return; }
    setSaving(true);
    try {
      // Sécurité côté client : seul un super admin peut faire varier le statut
      const payload = (isEdit && !isSuperAdmin) ? { ...form, status: originalStatusRef.current } : form;
      if (isEdit) await resourcesAPI.update(id, payload);
      else        await resourcesAPI.create(payload);
      toast.success(isEdit ? 'Ressource mise à jour' : 'Ressource créée');
      navigate(isEdit ? `/resources/${id}` : '/resources');
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };


  return (
    <div>
      <div className="page-header">
        <div className="page-header-left" style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate(isEdit ? `/resources/${id}` : '/resources')}><ArrowLeft size={18}/></button>
          <div>
            <h1>{isEdit ? 'Modifier la ressource' : 'Nouvelle ressource'}</h1>
            <p>{isEdit ? form.full_name : 'Ajouter un collaborateur ESN'}</p>
          </div>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => navigate(isEdit ? `/resources/${id}` : '/resources')}>Annuler</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleSubmit}>
            {saving ? <div className="spinner" style={{ width:14, height:14 }}/> : <Save size={14}/>} {isEdit ? 'Mettre à jour' : 'Créer'}
          </button>
        </div>
      </div>


      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        {/* Identité */}
        <div className="card">
          <div className="card-header"><span className="card-title">Identité</span></div>
          <div style={{ display:'grid', gap:12 }}>
            <div style={{ display:'grid', gridTemplateColumns:'80px 1fr', gap:10 }}>
              <Field label="Civilité" type="select" value={form.civilite} onChange={set('civilite')} options={['','M.','Mme']}/>
              <Field label="Nom complet *" value={form.full_name} onChange={set('full_name')} placeholder="Prénom Nom"/>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <Field label="Prénom" value={form.first_name} onChange={set('first_name')}/>
              <Field label="Nom" value={form.last_name} onChange={set('last_name')}/>
            </div>
            <Field label="Intitulé poste" value={form.title} onChange={set('title')} placeholder="Développeur Java Senior"/>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <Field label="Type" type="select" value={form.employee_type} onChange={set('employee_type')} options={EMP_TYPES}/>
              <Field
                label="Statut"
                type="select"
                value={form.status}
                onChange={set('status')}
                options={STATUSES}
                disabled={!isSuperAdmin}
                hint="Réservé aux super admins"
              />
            </div>
          </div>
        </div>


        {/* Contact */}
        <div className="card">
          <div className="card-header"><span className="card-title">Coordonnées</span></div>
          <div style={{ display:'grid', gap:12 }}>
            <Field label="Email" type="email" value={form.email} onChange={set('email')}/>
            <Field label="Téléphone" value={form.phone} onChange={set('phone')}/>
            <Field label="LinkedIn" value={form.linkedin_link} onChange={set('linkedin_link')} placeholder="https://linkedin.com/in/…"/>
            <Field label="Localisation" value={form.location} onChange={set('location')} placeholder="Paris, Île-de-France"/>
          </div>
        </div>


        {/* Mission */}
        <div className="card">
          <div className="card-header"><span className="card-title">Mission actuelle</span></div>
          <div style={{ display:'grid', gap:12 }}>
            <div>
              <label style={{ display:'block', fontSize:'0.8125rem', fontWeight:600, color:'var(--text-muted)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' }}>Client actuel</label>
              <select style={inp} value={form.current_client || ''} onChange={set('current_client')}>
                <option value="">-- Sélectionner un client --</option>
                {companies.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                <option value="__other__">Autre (saisir manuellement)</option>
              </select>
              {form.current_client === '__other__' && (
                <input style={{ ...inp, marginTop:6 }} placeholder="Nom du client" onChange={e => setForm(f => ({...f, current_client: e.target.value}))}/>
              )}
            </div>
            <Field label="Nom de la mission" value={form.current_mission} onChange={set('current_mission')}/>
            <Field label="Localisation mission" value={form.mission_location} onChange={set('mission_location')}/>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <Field label="Début mission" type="date" value={form.mission_start_date} onChange={set('mission_start_date')}/>
              <Field label="Fin mission" type="date" value={form.mission_end_date} onChange={set('mission_end_date')}/>
            </div>
            <Field label="Statut mission" type="select" value={form.mission_status} onChange={set('mission_status')} options={['', ...MISSION_STATUTS]}/>
            <Field label="Disponibilité" value={form.availability} onChange={set('availability')} placeholder="Immédiate, 1 mois…"/>
          </div>
        </div>


        {/* Contrat */}
        <div className="card">
          <div className="card-header"><span className="card-title">Contrat & Rémunération</span></div>
          <div style={{ display:'grid', gap:12 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <Field label="TJM" value={form.tjm} onChange={set('tjm')} placeholder="600€/j"/>
              <Field label="Salaire" value={form.salary} onChange={set('salary')} placeholder="45k€/an"/>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <Field label="Mode travail" type="select" value={form.work_mode} onChange={set('work_mode')} options={['',...WORK_MODES]}/>
              <Field label="Mobilité" value={form.mobility} onChange={set('mobility')} placeholder="Île-de-France, France…"/>
            </div>
            <Field label="Expérience" value={form.experience_years} onChange={set('experience_years')} placeholder="5 ans"/>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <Field label="Entrée ESN" type="date" value={form.start_date} onChange={set('start_date')}/>
              <Field label="Sortie ESN" type="date" value={form.end_date} onChange={set('end_date')}/>
            </div>
          </div>
        </div>


        {/* Interne */}
        <div className="card" style={{ gridColumn:'1/-1' }}>
          <div className="card-header"><span className="card-title">Responsables & Notes</span></div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Field label="Manager" value={form.manager_name} onChange={set('manager_name')}/>
            <Field label="Recruteur" value={form.recruiter_name} onChange={set('recruiter_name')}/>
            <Field label="Résumé profil" type="textarea" value={form.summary} onChange={set('summary')} placeholder="Points clés du profil…"/>
            <Field label="Notes internes" type="textarea" value={form.internal_comment} onChange={set('internal_comment')} placeholder="Infos confidentielles…"/>
          </div>
        </div>

        {/* Documents */}
        {(
          <div className="card" style={{ gridColumn:'1/-1' }}>
            <div className="card-header">
              <span className="card-title"><FileText size={14} style={{ verticalAlign:'middle', marginRight:6 }}/>Documents ({documents.length})</span>
              <label className="btn btn-ghost btn-sm" style={{ cursor:'pointer' }}>
                {uploading
                  ? <><div className="spinner" style={{ width:12, height:12 }}/> Upload…</>
                  : <><Upload size={13}/> Uploader</>}
                <input ref={fileInputRef} type="file" style={{ display:'none' }} accept=".pdf,.doc,.docx" onChange={handleUpload}/>
              </label>
            </div>
            {documents.length === 0 ? (
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{ border:'2px dashed var(--border)', borderRadius:10, padding:'24px', textAlign:'center',
                  cursor:'pointer', color:'var(--text-muted)', fontSize:'0.875rem', transition:'all 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#3B82F6'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <Upload size={24} style={{ marginBottom:8, opacity:0.4 }}/>
                <div>Cliquer pour uploader un CV ou document</div>
                <div style={{ fontSize:'0.75rem', marginTop:4 }}>PDF, DOC, DOCX</div>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {documents.map(doc => (
                  <div key={doc.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', border:'1px solid var(--border)', borderRadius:8 }}>
                    <FileText size={18} color="#64748B"/>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{doc.title || doc.file_name}</div>
                      <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{doc.document_type}</div>
                    </div>
                    {doc.file_url && <a href={doc.file_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">Télécharger</a>}
                    <button className="btn btn-ghost btn-icon btn-sm" style={{ color:'#DC2626' }} onClick={() => handleDeleteDoc(doc.id)}><Trash2 size={13}/></button>
                  </div>
                ))}
                <label className="btn btn-ghost btn-sm" style={{ cursor:'pointer', alignSelf:'flex-start' }}>
                  <Plus size={13}/> Ajouter un document
                  <input type="file" style={{ display:'none' }} accept=".pdf,.doc,.docx" onChange={handleUpload}/>
                </label>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function Field({ label, type='text', value, onChange, placeholder='', options=[], disabled=false, hint='' }) {
  const fieldStyle = disabled ? { ...inp, opacity:0.5, cursor:'not-allowed', background:'var(--bg-elevated)' } : inp;
  return (
    <div>
      <label style={{ display:'block', fontSize:'0.8125rem', fontWeight:600, color:'var(--text-muted)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</label>
      {type === 'select' ? (
        <select style={fieldStyle} value={value||''} onChange={onChange} disabled={disabled}>
          {options.map(o => <option key={o} value={o}>{o || '—'}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea style={{ ...fieldStyle, minHeight:80, resize:'vertical' }} value={value||''} onChange={onChange} placeholder={placeholder} disabled={disabled}/>
      ) : (
        <input style={fieldStyle} type={type} value={value||''} onChange={onChange} placeholder={placeholder} disabled={disabled}/>
      )}
      {disabled && hint && (
        <div style={{ fontSize:'0.6875rem', color:'var(--text-muted)', marginTop:4, fontStyle:'italic' }}>{hint}</div>
      )}
    </div>
  );
}
const inp = { width:'100%', padding:'9px 12px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:'0.875rem', boxSizing:'border-box', fontFamily:'inherit', outline:'none', background:'var(--bg-elevated)', color:'var(--text-primary)' };
